'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from 'next-themes';
import { memo, type ReactNode, Suspense, useCallback, useState } from 'react';

import { AnalyticsProvider } from '@air/analytics';
import { NetworkStatusInfo } from '@air/classes-network-status-info';
import { useConnectionStatusListener } from '@air/hook-use-connection-status-listener';
import { LaunchDarklyProvider } from '@air/plugin-ui';
import { TooltipProvider } from '@air/primitive-tooltip';
import { ModalProvider } from '@air/provider-modal';
import { ToastProvider } from '@air/provider-toast';

import { ThemeBridge } from '~/components/ThemeBridge';
import { ENV } from '~/constants/env';

if (ENV.API_ENDPOINT) {
  NetworkStatusInfo.endpoint = `${ENV.API_ENDPOINT}/health`;
}

export type ProvidersProps = {
  children: ReactNode;
};

const QUERY_STALE_TIME_MS = 3_000;

const makeQueryClient = () => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        staleTime: QUERY_STALE_TIME_MS,
      },
    },
  });
};

/** Loading fallback shown during initial Suspense (e.g., while useSearchParams resolves) */
const SuspenseFallback = memo(() => (
  <main aria-label="Loading" className="flex h-full items-center justify-center bg-white dark:bg-neutral-1">
    <div aria-busy="true" className="flex flex-col items-center gap-3" role="status">
      <div aria-hidden className="size-8 animate-spin rounded-full border-2 border-neutral-6 border-t-brand-9" />
      <p className="text-14 text-neutral-10">Loading...</p>
    </div>
  </main>
));

SuspenseFallback.displayName = 'SuspenseFallback';

/** Wires browser online/offline events into `NetworkStatusInfo` for the whole app. */
const ConnectionStatusBootstrap = memo(() => {
  const onConnectionChange = useCallback(() => {}, []);
  useConnectionStatusListener(onConnectionChange);

  return null;
});

ConnectionStatusBootstrap.displayName = 'ConnectionStatusBootstrap';

const Providers = memo(({ children }: ProvidersProps) => {
  const [queryClient] = useState(makeQueryClient);

  // Default to dark on first paint (most Premiere users are on Darkest);
  // `ThemeBridge` overrides via `setTheme` as soon as the host emits
  // its initial `ThemeChanged` from the `Ready` handshake. CSS-only
  // theming via `prefers-color-scheme` doesn't work here because
  // Premiere doesn't pipe theme into the embedded webview's CSS engine
  // — the JS handshake is required (host detects via
  // `document.theme.getCurrent()` / `document.theme.onUpdated`).
  return (
    <AnalyticsProvider analyticsKey={ENV.SEGMENT_KEY}>
      <ThemeProvider attribute="data-theme" defaultTheme="dark" disableTransitionOnChange>
        <LaunchDarklyProvider clientSideID={ENV.LAUNCH_DARKLY_KEY}>
          <QueryClientProvider client={queryClient}>
            <Suspense fallback={<SuspenseFallback />}>
              <ToastProvider>
                <TooltipProvider>
                  <ModalProvider>
                    <ConnectionStatusBootstrap />
                    <ThemeBridge />
                    {children}
                  </ModalProvider>
                </TooltipProvider>
              </ToastProvider>
            </Suspense>
          </QueryClientProvider>
        </LaunchDarklyProvider>
      </ThemeProvider>
    </AnalyticsProvider>
  );
});

Providers.displayName = 'Providers';

export default Providers;
